'use client'

import type { ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { FieldError } from '@/components/ui/field'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { TableAiLinks } from '@/components/blocks/tables'
import { cn } from '@/lib/utils'
import { BRAND_FILES, WEBSITE_LINKS, WRITING_TONES } from '@/constants/supporter-response-data'

import {
    ArrowRightIcon,
    BookOpenTextIcon,
    BooksIcon,
    FileArrowUpIcon,
    GlobeIcon
} from '@/components/icons/icon-set'

import {
    ResourceRow,
    UploadZone,
    WritingStyleNav,
    WritingStyleSection
} from '@/components/ui/writing-style'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

/*
 * GoodUnited / Writing-style settings. Figma: 33 / Templates / Supporter Response (Writing-style settings template).
 * The navigation (220px) beside the content on Colors/surface/subtle: Writing style (voice and tone), Nonprofit details,
 * Brand files and Add website with the links the AI reads (Table/AiLinks). Each section saves on its own.
 */
export const WRITING_STYLE_SECTIONS = [
    { id: 'writing-style-voice', label: 'Writing style' },
    { id: 'writing-style-details', label: 'Nonprofit details' },
    { id: 'writing-style-files', label: 'Brand files' },
    { id: 'writing-style-website', label: 'Add website' }
]

const VOICE_PLACEHOLDER = "Write in a warm, clear, and professional voice. Thank supporters for sharing their stories, acknowledge their experiences, and offer a helpful next step."
const DETAILS_PLACEHOLDER = "Our main priority is to reduce veteran suicide rates. We address challenges like PTSD, lack of resources, and social isolation. Our current goals include expanding our peer support network and increasing access to mental health services."

const isWebsite = (value: string) => /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(value)

const cleanWebsite = (value: string) => value.replace(/^https?:\/\//i, '').replace(/\/$/, '')

interface WritingStyleSettingsProps {
    heading?: ReactNode
    onClose?: () => void
    className?: string
}

export function WritingStyleSettings({ heading, onClose, className }: WritingStyleSettingsProps) {
    const { toast } = useToast()
    const scroller = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(WRITING_STYLE_SECTIONS[0].id)
    const [voice, setVoice] = useState({ value: '', saved: '' })
    const [tone, setTone] = useState({ value: WRITING_TONES[0], saved: WRITING_TONES[0] })
    const [details, setDetails] = useState({ value: '', saved: '' })
    const [files, setFiles] = useState<string[]>(BRAND_FILES)
    const [website, setWebsite] = useState('')
    const [websiteError, setWebsiteError] = useState('')
    const [websites, setWebsites] = useState<string[]>(['goodunited.com'])

    const voiceDirty = voice.value.trim() !== voice.saved || tone.value !== tone.saved
    const detailsDirty = details.value.trim() !== details.saved

    const jumpTo = (id: string) => {
        setActive(id)
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const trackScroll = () => {
        const container = scroller.current
        if (!container) return
        const top = container.getBoundingClientRect().top
        const current = WRITING_STYLE_SECTIONS.filter(section => {
            const element = document.getElementById(section.id)
            return element && element.getBoundingClientRect().top - top <= 96
        }).pop()
        const atEnd = container.scrollTop + container.clientHeight >= container.scrollHeight - 4
        setActive(atEnd ? WRITING_STYLE_SECTIONS[WRITING_STYLE_SECTIONS.length - 1].id : current?.id ?? WRITING_STYLE_SECTIONS[0].id)
    }

    const addWebsite = () => {
        const value = cleanWebsite(website.trim())
        if (!isWebsite(value)) {
            setWebsiteError('Enter a website address, such as goodunited.com.')
            return
        }
        if (websites.includes(value)) {
            setWebsiteError(`${value} is already added.`)
            return
        }
        setWebsites(current => [...current, value])
        setWebsite('')
        setWebsiteError('')
        toast({ title: 'Website added', description: `GoodUnited AI will review ${value}.` })
    }

    return (
        <div data-slot='writing-style-settings' className={cn('flex min-h-0 flex-col overflow-hidden bg-surface-subtle md:flex-row', className)}>
            <WritingStyleNav
                items={WRITING_STYLE_SECTIONS}
                active={active}
                onSelect={jumpTo}
                onClose={onClose}
                className='shrink-0 border-b border-border-default md:w-55 md:border-r md:border-b-0'
            />
            <div ref={scroller} onScroll={trackScroll} className='min-h-0 flex-1 overflow-y-auto'>
                <div className='flex flex-col gap-6 p-4 sm:p-6'>
                    <div className='flex flex-col gap-1'>
                        {heading ?? (
                            <h2 className='font-heading text-2xl font-semibold text-text-primary'>
                                Supporter Response writing style
                            </h2>
                        )}
                        <p className='text-sm text-text-secondary'>
                            Your organization’s voice and tone preferences
                        </p>
                    </div>

                    <WritingStyleSection
                        id={WRITING_STYLE_SECTIONS[0].id}
                        icon={<BookOpenTextIcon aria-hidden='true' />}
                        title='Writing style'
                        description='Your organization’s voice and tone preferences'
                    >
                        <Textarea
                            value={voice.value}
                            onChange={event => setVoice(current => ({ ...current, value: event.target.value }))}
                            placeholder={VOICE_PLACEHOLDER}
                            aria-label='Writing style'
                        />
                        <div className='flex flex-wrap items-center gap-2'>
                            <span id='writing-tone-label' className='text-sm text-text-secondary'>
                                Writing tone:
                            </span>
                            <Select value={tone.value} onValueChange={value => setTone(current => ({ ...current, value: String(value) }))}>
                                <SelectTrigger size='sm' className='w-37' aria-labelledby='writing-tone-label'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {WRITING_TONES.map(item => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <span className='flex-1' />
                            <Button
                                size='sm'
                                disabled={!voiceDirty}
                                onClick={() => {
                                    setVoice(current => ({ value: current.value.trim(), saved: current.value.trim() }))
                                    setTone(current => ({ ...current, saved: current.value }))
                                    toast({ title: 'Writing style saved', description: `New drafts use a ${tone.value.toLowerCase()} tone.` })
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    </WritingStyleSection>

                    <WritingStyleSection
                        id={WRITING_STYLE_SECTIONS[1].id}
                        icon={<BooksIcon aria-hidden='true' />}
                        title='Nonprofit details'
                        description='Share your top priorities or problems you’re trying to solve.'
                    >
                        <Textarea
                            value={details.value}
                            onChange={event => setDetails(current => ({ ...current, value: event.target.value }))}
                            placeholder={DETAILS_PLACEHOLDER}
                            aria-label='Nonprofit details'
                            className='min-h-24'
                        />
                        <Button
                            size='sm'
                            className='self-end'
                            disabled={!detailsDirty}
                            onClick={() => {
                                setDetails(current => ({ value: current.value.trim(), saved: current.value.trim() }))
                                toast({ title: 'Nonprofit details saved', description: 'GoodUnited AI uses them in every new draft.' })
                            }}
                        >
                            Apply
                        </Button>
                    </WritingStyleSection>

                    <WritingStyleSection
                        id={WRITING_STYLE_SECTIONS[2].id}
                        icon={<FileArrowUpIcon aria-hidden='true' />}
                        title='Brand files'
                        description='Upload reports, decks, or one-pagers that capture your mission.'
                    >
                        <Badge>
                            Optional
                        </Badge>
                        <UploadZone
                            accept='.pdf,.doc,.docx,.ppt,.pptx,.txt'
                            onFiles={picked => {
                                const names = picked.map(file => file.name).filter(name => !files.includes(name))
                                if (!names.length) return
                                setFiles(current => [...current, ...names])
                                toast({ title: names.length === 1 ? 'File added' : `${names.length} files added`, description: names.join(', ') })
                            }}
                        />
                        {files.length > 0 && (
                            <ul className='flex flex-col gap-2' aria-label='Brand files'>
                                {files.map(file => (
                                    <li key={file}>
                                        <ResourceRow label={file} onRemove={() => setFiles(current => current.filter(item => item !== file))} />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </WritingStyleSection>

                    <WritingStyleSection
                        id={WRITING_STYLE_SECTIONS[3].id}
                        icon={<GlobeIcon aria-hidden='true' />}
                        title='Add website'
                        description='Add your main site or campaign pages so we can review your content.'
                    >
                        <Badge variant='success'>
                            Required
                        </Badge>
                        <form
                            className='flex flex-col gap-1'
                            onSubmit={event => {
                                event.preventDefault()
                                addWebsite()
                            }}
                        >
                            <div className='flex items-center gap-2'>
                                <Input
                                    value={website}
                                    onChange={event => {
                                        setWebsite(event.target.value)
                                        setWebsiteError('')
                                    }}
                                    placeholder='goodunited.com'
                                    aria-label='Website address'
                                    aria-invalid={!!websiteError}
                                />
                                <Button type='submit' variant='outline' size='icon-sm' aria-label='Add website'>
                                    <ArrowRightIcon aria-hidden='true' />
                                </Button>
                            </div>
                            <FieldError>
                                {websiteError}
                            </FieldError>
                        </form>
                        {websites.length > 0
                            ? (
                                <ul className='flex flex-col gap-2' aria-label='Websites'>
                                    {websites.map(item => (
                                        <li key={item}>
                                            <ResourceRow type='website' label={item} onRemove={() => setWebsites(current => current.filter(entry => entry !== item))} />
                                        </li>
                                    ))}
                                </ul>
                            )
                            : (
                                <p className='text-sm text-status-error-foreground'>
                                    Add at least one website so GoodUnited AI can learn about your work.
                                </p>
                            )}
                        <TableAiLinks initialLinks={WEBSITE_LINKS} className='border border-border-subtle' />
                    </WritingStyleSection>
                </div>
            </div>
        </div>
    )
}

/* The writing-style settings in a Dialog sized to the Figma frame (916px, 16px corners), opened from Set up your writing style. */
export function WritingStyleDialog({ open, onOpenChange }: { open: boolean, onOpenChange: (open: boolean) => void }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='h-[calc(100dvh-2rem)] max-w-229 gap-0 overflow-hidden rounded-2xl bg-surface-subtle p-0 sm:p-0'>
                <WritingStyleSettings
                    className='h-full'
                    heading={(
                        <DialogTitle>
                            Supporter Response writing style
                        </DialogTitle>
                    )}
                    onClose={() => onOpenChange(false)}
                />
            </DialogContent>
        </Dialog>
    )
}
